import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Timer } from 'lucide-react'
import type { SlaPolicy } from '@/api/adminApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field, NativeSelect } from '@/components/common/Field'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ActiveBadge, FormError, ListState, RowActions, tdClass, thClass } from '@/components/admin/AdminShared'
import { useAdminResource } from '@/hooks/useAdmin'
import { useCustomers, useProducts } from '@/hooks/useDirectory'
import {
  EMPTY_POLICY_FORM,
  PAUSABLE_STATUSES,
  formToPolicy,
  formatMinutes,
  policyToForm,
  validatePolicy,
  type SlaPolicyForm,
} from '@/lib/adminForms'
import { FILTER_PRIORITIES } from '@/lib/queueFilters'
import { priorityLabel, statusLabel } from '@/lib/ticketLabels'

export function PoliciesTab() {
  const { t } = useTranslation()
  const { list, save, remove } = useAdminResource('slaPolicies', true)
  const calendars = useAdminResource('calendars', true).list
  const customers = useCustomers()
  const products = useProducts()
  const [editing, setEditing] = useState<SlaPolicy | 'new' | null>(null)
  const [deleting, setDeleting] = useState<SlaPolicy | null>(null)

  const nameOf = <T extends { id: string; name: string }>(rows: T[] | undefined, id: string | null) =>
    id ? (rows?.find((r) => r.id === id)?.name ?? id.slice(0, 8)) : null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('admin.add.policies')}</Button>
      </div>
      <ListState query={list}>
        {list.data && list.data.length === 0 ? (
          <EmptyState compact icon={Timer} message={t('admin.empty')} />
        ) : (
          <table className="w-full border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={thClass}>{t('admin.policy.name')}</th>
                <th className={thClass}>{t('admin.policy.priority')}</th>
                <th className={thClass}>{t('admin.policy.targetsHeader')}</th>
                <th className={thClass}>{t('admin.policy.calendar')}</th>
                <th className={thClass}>{t('admin.policy.scope')}</th>
                <th className={thClass} />
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {list.data?.map((p) => (
                <tr key={p.id} className="border-b border-line">
                  <td className={`${tdClass} font-medium text-ink-strong`}>{p.name}</td>
                  <td className={`${tdClass} whitespace-nowrap`}>{priorityLabel(p.priority)}</td>
                  <td className={`${tdClass} font-mono`}>
                    {formatMinutes(p.firstResponseMins)} / {formatMinutes(p.resolutionMins)}
                  </td>
                  <td className={tdClass}>{p.calendar?.name ?? nameOf(calendars.data, p.calendarId)}</td>
                  <td className={tdClass}>
                    {[p.customer?.name ?? nameOf(customers.data, p.customerId), p.product?.name ?? nameOf(products.data, p.productId)]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td className={tdClass}>
                    <ActiveBadge active={p.active} />
                  </td>
                  <td className={tdClass}>
                    <RowActions onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListState>

      {editing ? (
        <PolicyDialog
          policy={editing === 'new' ? null : editing}
          calendars={calendars.data ?? []}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) =>
            save.mutate(
              { id: editing === 'new' ? undefined : editing.id, input },
              {
                onSuccess: () => setEditing(null),
              },
            )
          }
          customers={customers.data ?? []}
          products={products.data ?? []}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title={t('admin.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('admin.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </div>
  )
}

function PolicyDialog({
  policy,
  calendars,
  customers,
  products,
  pending,
  error,
  onClose,
  onSave,
}: {
  policy: SlaPolicy | null
  calendars: { id: string; name: string }[]
  customers: { id: string; name: string }[]
  products: { id: string; name: string }[]
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: ReturnType<typeof formToPolicy>) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<SlaPolicyForm>(() => (policy ? policyToForm(policy) : { ...EMPTY_POLICY_FORM, calendarId: calendars[0]?.id ?? '' }))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const set = <K extends keyof SlaPolicyForm>(k: K, v: SlaPolicyForm[K]) => setForm((f) => ({ ...f, [k]: v }))
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const found = validatePolicy(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(formToPolicy(form))
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{policy ? t('admin.editTitle.policies') : t('admin.createTitle.policies')}</DialogTitle>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field id="sp-name" label={t('admin.policy.name')} error={err('name')} className="col-span-2">
            <Input id="sp-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field id="sp-priority" label={t('admin.policy.priority')} error={err('priority')}>
            <NativeSelect id="sp-priority" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
              {FILTER_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityLabel(p)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="sp-calendar" label={t('admin.policy.calendar')} error={err('calendarId')}>
            <NativeSelect id="sp-calendar" value={form.calendarId} onChange={(e) => set('calendarId', e.target.value)}>
              <option value="">{t('common.select')}</option>
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="sp-first" label={t('admin.policy.firstResponse')} error={err('firstResponseMins')}>
            <Input id="sp-first" inputMode="numeric" value={form.firstResponseMins} onChange={(e) => set('firstResponseMins', e.target.value)} />
          </Field>
          <Field id="sp-res" label={t('admin.policy.resolution')} error={err('resolutionMins')}>
            <Input id="sp-res" inputMode="numeric" value={form.resolutionMins} onChange={(e) => set('resolutionMins', e.target.value)} />
          </Field>
          <Field id="sp-warn" label={t('admin.policy.warnAt')} error={err('warnAtPct')}>
            <Input id="sp-warn" inputMode="numeric" value={form.warnAtPct} onChange={(e) => set('warnAtPct', e.target.value)} />
          </Field>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-meta text-ink">
              <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
              {t('admin.active')}
            </label>
          </div>
          <Field id="sp-customer" label={t('admin.policy.customer')}>
            <NativeSelect id="sp-customer" value={form.customerId} onChange={(e) => set('customerId', e.target.value)}>
              <option value="">{t('admin.policy.anyCustomer')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="sp-product" label={t('admin.policy.product')}>
            <NativeSelect id="sp-product" value={form.productId} onChange={(e) => set('productId', e.target.value)}>
              <option value="">{t('admin.policy.anyProduct')}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <fieldset className="col-span-2 flex flex-col gap-2">
            <legend className="pb-1.5 text-meta font-medium text-ink">{t('admin.policy.pauseStatuses')}</legend>
            <div className="flex flex-wrap gap-4">
              {PAUSABLE_STATUSES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-meta text-ink">
                  <input
                    type="checkbox"
                    checked={form.pauseStatuses.includes(s)}
                    onChange={(e) =>
                      set('pauseStatuses', e.target.checked ? [...form.pauseStatuses, s] : form.pauseStatuses.filter((x) => x !== s))
                    }
                  />
                  {statusLabel(s)}
                </label>
              ))}
            </div>
          </fieldset>
          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
